import { useCallback, useRef, useState } from 'react';
import { Trans } from 'react-i18next';
import { Dropdown, type Option } from '@/UI/Dropdown/Dropdown';
import { openIntercom } from '@/providers/intercom';

interface SelectInputProps<T extends string> {
  disabled: boolean;
  value: T;
  helperText?: string;
  noOptionsText?: string;
  onReset: () => void;
  onSubmit: (value: T, ongoing: boolean) => void;
  options: Option<T>[];
  search?: boolean;
  size?: 'small' | 'large';
}
export function SelectInput<T extends string>({
  disabled,
  value,
  helperText,
  noOptionsText,
  onReset,
  onSubmit,
  options,
  search = false,
  size = 'small',
}: SelectInputProps<T>) {
  const openRef = useRef(false);
  const [confirmedValue, setConfirmedValue] = useState(value);

  if (!openRef.current && confirmedValue !== value) {
    setConfirmedValue(value);
  }

  const handleOpen = useCallback(() => {
    openRef.current = true;
    setConfirmedValue(value);
  }, [value]);

  const handleClose = useCallback(() => {
    openRef.current = false;
    onReset();
  }, [onReset]);

  const handleChange = useCallback(
    (option: Option<T>) => {
      openRef.current = false;
      setConfirmedValue(option.value);
      onSubmit(option.value, false);
    },
    [onSubmit],
  );
  const handleOngoingChange = useCallback(
    (option: Option<T> | null) => {
      if (option?.value) {
        onSubmit(option.value, true);
      } else {
        onReset();
      }
    },
    [onSubmit, onReset],
  );

  return (
    <Dropdown
      disabled={disabled}
      helperText={
        helperText ? (
          <Trans
            i18nKey={helperText}
            components={{
              supportEmail: <a onClick={openIntercom} style={{ color: 'inherit', textDecoration: 'underline' }} />,
            }}
          />
        ) : undefined
      }
      noOptionsText={noOptionsText ? <Trans i18nKey={noOptionsText} /> : undefined}
      onChange={handleChange}
      onClose={handleClose}
      onHighlightedOptionChange={handleOngoingChange}
      onOpen={handleOpen}
      options={options}
      placement="auto"
      matchTriggerWidth={size === 'large'}
      search={search}
      confirmedValue={confirmedValue}
      value={value}
      width={size === 'small' ? '88px' : '180px'}
    />
  );
}
